/**
 * Sick leave, said per year without misstating what the law gives.
 *
 * South Africa's entitlement is not annual. The BCEA gives a worker the days
 * they would normally work in six weeks over each 36-month cycle — 30 days for
 * a five-day week — and they may take all 30 in the first year of that cycle
 * if they are ill enough to. Nothing accrues monthly and nothing lapses
 * yearly.
 *
 * So the yearly figure here is an average for reading, not an allowance. It
 * exists because "30 days per 3 years" is a hard sentence to hold in your head
 * next to "15 days annual leave", and a worker comparing the two deserves the
 * same unit. Every screen that shows it also shows the cycle it came from,
 * because the average is the friendly number and the cycle is the true one.
 */

/** The yearly average of a cycle entitlement. 30 days over 36 months is 10. */
export function sickPerYear(days: number, cycleMonths: number): number {
  if (!Number.isFinite(days) || !Number.isFinite(cycleMonths) || cycleMonths <= 0) return 0;
  const years = cycleMonths / 12;
  const perYear = days / years;
  // One decimal, and only when it earns it: 10 rather than 10.0, 6.7 rather
  // than 6.6666666.
  return Math.round(perYear * 10) / 10;
}

/** "30 days over 3 years", or null when the cycle already is a year and the
 *  yearly figure is the whole truth on its own. */
export function sickCycleNote(days: number, cycleMonths: number): string | null {
  if (cycleMonths === 12) return null;
  const years = cycleMonths / 12;
  const span = Number.isInteger(years)
    ? `${years} year${years === 1 ? "" : "s"}`
    : `${cycleMonths} months`;
  return `${days} days over ${span}`;
}

/** "10 days a year" — the headline figure, in the unit annual leave uses. */
export function sickPerYearLabel(days: number, cycleMonths: number): string {
  const n = sickPerYear(days, cycleMonths);
  return `${n} day${n === 1 ? "" : "s"} a year`;
}
